// Google Analytics 4 for feature usage. Everything goes through this module; components
// never call gtag directly.
//
// Privacy rules:
// - Off unless REACT_APP_GA_MEASUREMENT_ID is set (see config.js): no script, no calls.
// - URLs are cleaned before Google sees them: query strings are dropped (?personid=, the
//   People list's ?name=, Cognito's ?code=&state=) and share tokens are replaced, because
//   a token grants access to a profile. The cleaned URL is also set as the default
//   page_location/page_referrer, so events never carry the raw URL.
// - Only known event names and a small set of parameters with short, non-personal values
//   are sent. No names, emails, person IDs or free text.
// Analytics loads without a consent banner (a deliberate product decision).

export const ANALYTICS_EVENTS = {
  EDIT_STARTED: 'profile_edit_started',
  PROFILE_SAVED: 'profile_saved',
  PROFILE_SAVED_WITH_COMPANIES: 'profile_saved_with_company_logos',
  AI_ASSIST_USED: 'ai_assist_used',
  LONG_TEXT_SUMMARISE: 'long_text_summarise',
  LONG_TEXT_SAVE_ANYWAY: 'long_text_save_anyway',
  SHARING_PAGE_OPENED: 'sharing_page_opened',
  SHARING_LINK_COPIED: 'sharing_link_copied',
  FINE_TUNE_STARTED: 'fine_tune_started',
  PROFILE_EXPORTED: 'profile_exported',
  PEOPLE_SEARCHED: 'people_searched'
};

// Parameter values must match this pattern (short labels such as "people_list" or
// "name,skills"), which rules out free text and IDs.
const ALLOWED_PARAMS = new Set(['placement', 'filters']);
const SAFE_VALUE = /^[a-z_,]{1,60}$/;

const KNOWN_EVENTS = new Set(Object.values(ANALYTICS_EVENTS));
const SHARE_PATH = /^\/share\/[^/]+/;

let enabled = false;

const gtag = (...args) => {
  if (enabled && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

// Path only (no query string or hash), with share tokens replaced.
export const sanitizePath = (pathname) => {
  const path = typeof pathname === 'string' && pathname.startsWith('/') ? pathname : '/';
  return path.replace(SHARE_PATH, '/share/:token');
};

const sanitizeUrl = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${sanitizePath(parsed.pathname)}`;
  } catch (_) {
    return '';
  }
};

export const isAnalyticsEnabled = () => enabled;

export const initAnalytics = (measurementId) => {
  if (enabled || !measurementId) {
    return false;
  }
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtagShim() {
    // gtag.js expects the arguments object itself.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  enabled = true;
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
    page_location: sanitizeUrl(window.location.href),
    page_referrer: sanitizeUrl(document.referrer)
  });
  return true;
};

export const setAnalyticsUserType = (userType) => {
  if (userType === 'admin' || userType === 'user') {
    gtag('set', 'user_properties', { user_type: userType });
  }
};

export const trackPageView = (pathname) => {
  if (!enabled) {
    return;
  }
  const pagePath = sanitizePath(pathname);
  const pageLocation = `${window.location.origin}${pagePath}`;
  // Becomes the default for later events too, so they never carry the raw URL.
  gtag('set', { page_location: pageLocation, page_referrer: '' });
  gtag('event', 'page_view', { page_location: pageLocation, page_path: pagePath });
};

export const trackEvent = (name, params = {}) => {
  if (!enabled || !KNOWN_EVENTS.has(name)) {
    return;
  }
  const safeParams = Object.fromEntries(
    Object.entries(params || {}).filter(
      ([key, value]) => ALLOWED_PARAMS.has(key) && typeof value === 'string' && SAFE_VALUE.test(value)
    )
  );
  gtag('event', name, safeParams);
};

// For tests only.
export const resetAnalyticsForTests = () => {
  enabled = false;
};
